import { isCareProAbbreviation } from "../constants/abbreviations";

const TOKEN_SPLIT = /[\s_-]+/;

function formatWordForDisplay(word: string): string {
  if (word.length === 0) return "";
  const hasLetter = /[a-zA-Z]/.test(word);
  if (!hasLetter) return word;
  if (isCareProAbbreviation(word)) {
    return word.replace(/[a-zA-Z]+/g, (letters) => letters.toUpperCase());
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Format lead source / channel codes and labels for display. Allowlisted
 * abbreviations (EMR, PDC, MHEA, etc.) render uppercase regardless of DB casing.
 * Other tokens use title case. Display-only.
 */
export function formatAbbreviationForDisplay(
  value: string | null | undefined
): string {
  if (value == null || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";

  if (isCareProAbbreviation(trimmed)) {
    return trimmed.replace(/[a-zA-Z]+/g, (letters) => letters.toUpperCase());
  }

  return trimmed
    .split(TOKEN_SPLIT)
    .filter((part) => part.length > 0)
    .map(formatWordForDisplay)
    .join(" ");
}

/**
 * Format a string for display as Title Case (e.g. "ORTHOPAEDICS" -> "Orthopaedics",
 * "DR. ROHIT BANSAL" -> "Dr. Rohit Bansal"). Allowlisted abbreviations stay
 * uppercase (e.g. "MHEA" -> "MHEA", not "Mhea"). Display-only.
 */
export function toTitleCase(value: string | null | undefined): string {
  if (value == null || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  return trimmed
    .split(/\s+/)
    .map((word) =>
      /[_-]/.test(word)
        ? formatAbbreviationForDisplay(word)
        : formatWordForDisplay(word)
    )
    .join(" ");
}

/**
 * Format a patient/contact name for display as Title Case (e.g. "Deepak Verma").
 * Display-only; does not modify stored values or API payloads.
 */
export function formatPatientNameForDisplay(
  name: string | null | undefined
): string {
  return toTitleCase(name);
}

const CHANNEL_UPPERCASE_LABELS = new Set(["emr", "pdc"]);

/**
 * Format Channel dropdown labels only. Underscores/hyphens become spaces and
 * each word is title-cased (e.g. Contact_us_dr_appointment → Contact Us Dr
 * Appointment). EMR and PDC stay uppercase. Ids / API values stay unchanged.
 */
export function formatChannelLabelForDisplay(
  value: string | null | undefined
): string {
  if (value == null || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  return trimmed
    .split(TOKEN_SPLIT)
    .filter((part) => part.length > 0)
    .map((word) => {
      if (CHANNEL_UPPERCASE_LABELS.has(word.toLowerCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Format API enum-style strings (e.g. OPEN, MEDIUM, IN_PROGRESS) for UI: first
 * character uppercase, the rest lowercase. Underscores become spaces.
 */
export function formatEnumLabelForDisplay(
  value: string | null | undefined
): string {
  if (value == null || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  const withSpaces = trimmed.replace(/_/g, " ");
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
}
